import { useEffect, useState } from 'react'

import { internalDropdowns } from '@/entities/dropdowns/models/InternalDropdowns'
import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'

import { DepotFormValues, DriverFormValues, GeneralPreferencesFormValues, GroupFormValues, RunFormValues } from '../models/OnboardingFormFields'

// LocalStorage keys
export const ONBOARDING_STATE_KEY = 'fastmile_onboarding_state'
export const ACTIVE_STEP_KEY = 'fastmile_onboarding_step'

export type OnboardingState = {
  generalPreferences: GeneralPreferencesFormValues
  groupData: GroupFormValues
  depotData: DepotFormValues
  runData: RunFormValues
  driverData: DriverFormValues
  completed: { [k: number]: boolean }
  skipped: number[]
}

const defaultGeneralPreferences: GeneralPreferencesFormValues = {
  businessModel: BusinessModelEnum.Depot as 1,
  distanceUnit: 'km',
  weightUnit: 'kg',
  volumeUnit: 'cbm',
}

const defaultGroupData: GroupFormValues = {
  id: '',
  name: '',
  orderHasLineItem: false,
  isActive: true,
  timeZone: internalDropdowns.timeZones.find(item => item.value === 'AUS Eastern Standard Time') || null,
  mapCenter: {
    latitude: -37.810272,
    longitude: 144.962646,
    zoomLevel: 12,
  },
}

const defaultDepotData: DepotFormValues = {
  id: '',
  groupId: null, // Reference to the group ID, not the full object
  externalId: '',
  name: '',
  uiColor: internalDropdowns.htmlColors[0],
  siteTime: null,
  loadTime: null,
  unloadTime: null,
  isActive: true,
  address: {
    street: '',
    suburb: '',
    postcode: '',
    state: '',
    country: 'Australia',
    x: 144.962646,
    y: -37.810272,
  },
}

const defaultRunData: RunFormValues = {
  id: '',
  name: '',
  description: '',
  groupId: null,
  startDepotId: null,
  finishDepotId: null,
  uiColorId: internalDropdowns.htmlColors[0],
  weightCapacity: 1000,
  volumeCapacity: 10,
  isActive: true,
  attributeIds: [],
}

const defaultDriverData: DriverFormValues = {
  id: '',
  login: '',
  name: '',
  password: '',
  employeeNumber: '',
  email: '',
  mobile: '',
  groupIds: [],
  runIds: [],
  isActive: true,
}


export const useOnboardingState = () => {
  const [activeStep, setActiveStep] = useState(0)
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({})
  const [skipped, setSkipped] = useState<Set<number>>(new Set())

  const [generalPreferences, setGeneralPreferences] = useState<GeneralPreferencesFormValues>(defaultGeneralPreferences)
  const [groupData, setGroupData] = useState<GroupFormValues>(defaultGroupData)
  const [depotData, setDepotData] = useState<DepotFormValues>(defaultDepotData)
  const [runData, setRunData] = useState<RunFormValues>(defaultRunData)
  const [driverData, setDriverData] = useState<DriverFormValues>(defaultDriverData)
  const [isDemoGroupCreationEnabled, setIsDemoGroupCreationEnabled] = useState(true)


  // Load saved state from localStorage on initial render
  useEffect(() => {
    const savedStep = localStorage.getItem(ACTIVE_STEP_KEY)

    if (savedStep) {
      setActiveStep(parseInt(savedStep, 10))
    }

    const savedState = localStorage.getItem(ONBOARDING_STATE_KEY)

    if (savedState) {
      const parsedState: OnboardingState = JSON.parse(savedState)

      // Load each form's data
      if (parsedState.generalPreferences) setGeneralPreferences(parsedState.generalPreferences)
      if (parsedState.groupData) setGroupData(parsedState.groupData)
      if (parsedState.depotData) setDepotData(parsedState.depotData)
      if (parsedState.runData) setRunData(parsedState.runData)
      if (parsedState.driverData) setDriverData(parsedState.driverData)

      // Restore completed steps
      if (parsedState.completed) setCompleted(parsedState.completed)

      // Restore skipped steps
      if (parsedState.skipped) {
        const skippedSet = new Set(parsedState.skipped)

        setSkipped(skippedSet)
      }
    }
  }, [])

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(completed).length > 0 || activeStep > 0) {
      const onboardingState: OnboardingState = {
        generalPreferences,
        groupData,
        depotData,
        runData,
        driverData,
        completed,
        skipped: Array.from(skipped),
      }

      localStorage.setItem(ONBOARDING_STATE_KEY, JSON.stringify(onboardingState))
      localStorage.setItem(ACTIVE_STEP_KEY, activeStep.toString())
    }
  }, [generalPreferences, groupData, depotData, runData, driverData, completed, skipped, activeStep])

  return {
    activeStep,
    setActiveStep,
    completed,
    setCompleted,
    skipped,
    setSkipped,
    generalPreferences,
    setGeneralPreferences,
    groupData,
    setGroupData,
    depotData,
    setDepotData,
    runData,
    setRunData,
    driverData,
    setDriverData,
    isDemoGroupCreationEnabled,
    setIsDemoGroupCreationEnabled,
  }
}